import { useDeferredValue, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppSelector } from "redux/hooks";
import { getCoreRowModel, getExpandedRowModel, useReactTable } from "@tanstack/react-table";
import { usePersistedTableState } from "components/table/usePersistedTableState";
import { RT8ColumnSelector } from "components/core/blocks/MetricSelector";
import { SearchInput } from "components/fields/base/TextField";
import { RT8Pagination } from "components/table/Pagination";
import { Table } from "components/table/Table";
import { idsToVisibilityState, resetTable, selectionStateToSelectedIds } from "components/table/utils";
import { RT8FilterWidget } from "views/merchandise/tables/FilterWidget";
import { Button } from "components/core/basic/Button";
import { MdRotateLeft } from "react-icons/md";
import { INVENTORY_STATE_OPTIONS, SALE_STATE_OPTIONS } from "components/custom/merchandise/constants";
import { BulkActionButton } from "components/table/BulkActionButton";
import { createColumnBuilder } from "components/table/columnBuilder";
import { ProductCell } from "views/merchandise/cells/ProductCell";
import { useStandardEntityMetrics } from "views/merchandise/tables/metricUtils";
import { HStack, VStack, Wrap } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import AdvertisementProductExpander from "views/advertising/AdvertisementProductExpander";
import { type ProductAdV4Row, useGetV4ProductAdReport } from "modules/perf_reports/api/product_ad/v4ProductAdReport";
import { Switch } from "components/fields/base/Switch";
import { useUpdateProductAdState } from "modules/advertising/api/useUpdateProductAdState";
import { PRODUCT_AD_ENABLED, PRODUCT_AD_PAUSED } from "components/core/blocks/AdPlan/constants";
import { formatServingStatus } from "./ProductAdsTable";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import { PRODUCT_AD_SERVING_STATUS_OPTIONS } from "views/advertising/productLifecycleFilters";
import { useGetV4ProductReportOptions } from "modules/perf_reports/api/product/v4ProductReportOptions";
import { toTitleCase } from "utils/formatText";
import { DevErrorDisplay } from "components/core/blocks/DevErrorDisplay";

const columnHelper = createColumnBuilder<ProductAdV4Row>();

const ProductAdsTable2 = ({ adId }: { adId?: number }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const standardMetricIds = useStandardEntityMetrics("productAd");
  const stateMutation = useUpdateProductAdState();

  const columns = useMemo(
    () => [
      columnHelper.accessor("product_title", {
        header: () => <span>Product</span>,
        enableHiding: false,
        minSize: 100,
        size: 600,
        cell: (props) => {
          const { product_title } = props.row.original;

          return (
            <ProductCell
              original={{
                ...props.row.original,
                product_id: props.row.original.gvproduct_id.toString(),
                cellContext: "merchandising",
              }}
              value={product_title}
              showInventoryBadge={false}
              className={css({ marginRight: "auto", overflowX: "hidden" })}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            label: "Product Title",
          },
        },
      }),
      // Invisible columns to support filtering
      columnHelper.accessor((row) => row, {
        id: "inventory_stock_state",
        header: "Inventory State",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "sales_state",
        header: "Sales State",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      columnHelper.accessor((row) => row, {
        id: "tags",
        header: "Tags",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "multi-select",
          },
        },
      }),

      columnHelper.accessor((row) => row, {
        id: "variations",
        header: "Variants",
        enableHiding: false,
        meta: {
          columnFilterOptions: {
            format: "select",
          },
        },
      }),
      // Invisible columns end
      columnHelper.accessor("user_status", {
        id: "user_status",
        header: "State",
        enableResizing: false,
        size: 100,
        cell: function StateCell({ getValue, row }) {
          const value = Boolean(getValue() === "enabled");
          const inlineStateMutation = useUpdateProductAdState();

          // Updating cache from mutation is a PITA, since query key is super complicated. Let's just use a local state
          const [switchState, setSwitchState] = useState(value);
          if (value !== switchState && !inlineStateMutation.isPending) {
            setSwitchState(value);
          }

          return (
            <Switch
              isSelected={switchState}
              isPending={inlineStateMutation.isPending}
              onChange={(isSelected) => {
                inlineStateMutation.mutate(
                  {
                    custom_api_request: "pause_product_ad",
                    products: [row.original.gvproduct_id],
                    id: row.original.gvadvertisement_id,
                    state: isSelected ? PRODUCT_AD_ENABLED : PRODUCT_AD_PAUSED,
                  },
                  {
                    onSuccess: () => {
                      setSwitchState(isSelected);
                    },
                  },
                );
              }}
            />
          );
        },
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
        },
      }),
      columnHelper.accessor("serving_status", {
        id: "serving_status",
        header: "Serving Status",
        size: 70,
        cell: ({ getValue }) => formatServingStatus(getValue()),
        meta: {
          columnFilterOptions: {
            format: "select",
          },
          columnSelectorOptions: {
            grouping: "configuration",
          },
        },
      }),
      ...standardMetricIds.map((metricId) => {
        return columnHelper.metric(metricId);
      }),
    ],
    [standardMetricIds],
  );

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "user_status",
    "serving_status",
    "acos",
    "ad_sales",
    "ad_spend",
    "impressions",
    "clicks",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "adProductTable",
    initialState: {
      columnVisibility: defaultHiddenCols,
      columnOrder: defaultSelectedColumns,
      columnPinning: {
        left: ["product_title"],
      },
      sorting: [{ id: "sales", desc: true }],
    },
  });

  const deferredSearch = useDeferredValue(state.globalFilter);

  const byProductAdOptionsGetter = useGetV4ProductAdReport(adId);

  const productReportOptionsQuery = useQuery(useGetV4ProductReportOptions());
  const productAdByProductAdQuery = useQuery(
    byProductAdOptionsGetter({
      pagination: state.pagination,
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
  );

  useQuery({
    ...byProductAdOptionsGetter({
      pagination: { pageIndex: state.pagination.pageIndex + 1, pageSize: state.pagination.pageSize },
      sorting: state.sorting,
      filters: state.columnFilters,
      search: deferredSearch,
    }),
    staleTime: 10000,
    enabled: !!adId && productAdByProductAdQuery.isSuccess,
  });

  const tagOptions = useMemo(
    () => [
      { value: "all", label: "All Tags" },
      ...(productReportOptionsQuery.data?.data.tags.map((tag) => {
        return {
          value: tag,
          label: tag,
        };
      }) ?? []),
    ],
    [productReportOptionsQuery.data?.data.tags],
  );

  const variantOptions = useMemo(() => {
    const { variations } = productReportOptionsQuery.data?.data ?? {};

    if (!variations) {
      return [];
    }

    return [
      { value: "all", label: "All Variants" },
      ...Object.keys(variations)
        .map((variationCategory) => {
          return variations[variationCategory]?.map((variation) => ({
            label: `${toTitleCase(variationCategory)}: ${variation}`,
            value: { [variationCategory]: variation },
          }));
        })
        .flat(),
    ];
  }, [productReportOptionsQuery.data?.data]);

  const formattedData = useMemo(() => {
    if (!productAdByProductAdQuery.data?.data) {
      return [];
    }
    return productAdByProductAdQuery.data.data.map((row) => {
      return {
        ...row,
        product_id: row.gvproduct_id,
        advertisement_id: row.gvadvertisement_id,
        total_cost: row.cost,
        total_cost_prev: row.total_cost_prev,
      };
    });
  }, [productAdByProductAdQuery.data?.data]);

  const table = useReactTable({
    data: formattedData,
    columns,
    defaultColumn: {
      filterFn: () => true,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
    getRowId: (row) => row.gvproduct_id.toString(),
    enableRowSelection: true,
    rowCount: productAdByProductAdQuery.data?.count || 0,
    initialState: initialState,
    state: state,
    ...setters,
    manualPagination: true,
    meta: {
      showSkeleton:
        productAdByProductAdQuery.isFetching &&
        (!productAdByProductAdQuery.data || productAdByProductAdQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        inventory_stock_state: [
          {
            value: "all",
            label: "All Inventory States",
            description: "Show products with any inventory state",
          },
          {
            value: "IN_STOCK_SCARCE",
            label: "Low Stock",
            description: "Product stock is running low.",
          },
          ...Object.values(INVENTORY_STATE_OPTIONS)
            .filter((option) => option && option.value !== "LOW_STOCK")
            .filter(Boolean),
        ],
        sales_state: [
          {
            value: "all",
            label: "All Sales States",
          },
          ...SALE_STATE_OPTIONS.filter((option) => option !== undefined),
        ],
        serving_status: [
          {
            value: "all",
            label: "All Statuses",
          },
          ...PRODUCT_AD_SERVING_STATUS_OPTIONS,
        ],
        tags: tagOptions,
        variations: variantOptions.filter(Boolean),
        user_status: [
          {
            value: "all",
            label: "All Statuses",
          },
          {
            value: "enabled",
            label: "Enabled",
          },
          {
            value: "paused",
            label: "Paused",
          },
        ],
      },
    },
  });

  const selected = selectionStateToSelectedIds(table.getState().rowSelection);

  if (productAdByProductAdQuery.isError) {
    return <DevErrorDisplay userMessage={"Error loading stats"} error={productAdByProductAdQuery.error} />;
  }

  return (
    <div>
      <Wrap justifyContent={"end"} width={"100%"} padding={"small"} fontSize={"large"}>
        <BulkActionButton
          selected={selected}
          options={[
            {
              label: `Pause Product Advertisement${selected.length > 1 ? "s" : ""}`,
              action: () => {
                if (adId) {
                  stateMutation.mutate({
                    custom_api_request: "pause_product_ad",
                    products: selected.map((id) => Number(id)),
                    id: adId,
                    state: PRODUCT_AD_PAUSED,
                  });
                }
              },
              confirmationMessage: (
                <p>
                  Are you sure you want to pause the {selected.length > 1 ? selected.length : ""} selected product
                  {selected.length > 1 ? "s" : ""}?
                </p>
              ),
            },
            {
              label: `Enable Product Advertisement${selected.length > 1 ? "s" : ""}`,
              action: () => {
                if (adId) {
                  stateMutation.mutate({
                    custom_api_request: "pause_product_ad",
                    products: selected.map((id) => Number(id)),
                    id: adId,
                    state: PRODUCT_AD_ENABLED,
                  });
                }
              },
              confirmationMessage: <p>Are you sure you want to enable the selected products?</p>,
            },
          ]}
        />
      </Wrap>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={"Search"}
              isExpandable
              className={css({ flex: "0 1 350px" })}
              value={table.getState().globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => {
          return (
            <div className={css({ padding: "medium", backgroundColor: "rgb(239, 239, 239)" })}>
              <ExpanderContainer
                className={css({
                  maxWidth: "calc(100vw - 140px)!important",
                  overflow: "hidden",
                })}
              >
                <AdvertisementProductExpander
                  row={{ _original: row.original }}
                  marketPlace={marketPlace}
                  channel={channel}
                  adId={row.original.gvadvertisement_id}
                />
              </ExpanderContainer>
            </div>
          );
        }}
      />
    </div>
  );
};

export default ProductAdsTable2;
